import React, { useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Input,
  FormGroup,
  Label,
  Button,
  UncontrolledAlert,
} from "reactstrap";

// Import Editor
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';

// FlatPickr
import "flatpickr/dist/themes/material_blue.css";
import FlatPickr from "react-flatpickr";

// Import Breadcrumb
import Breadcrumbs from "../../components/Common/Breadcrumb";

// Modal
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";

const TMcreate = () => {
  document.title = "AI Threat Modelling";

  const inpRow = [{ name: "", file: "" }];
  const [initiated, setinitiated] = useState(new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' }));
  const [lastScan, setlastScan] = useState(new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' }));
  const [inputFields, setinputFields] = useState(inpRow);

  // Define OSI layers with names
  const osiLayers = [
    { id: 7, name: "Application" },
    { id: 6, name: "Presentation" },
    { id: 5, name: "Session" },
    { id: 4, name: "Transport" },
    { id: 3, name: "Network" },
    { id: 2, name: "Data Link" },
    { id: 1, name: "Physical" }
  ];

  const [taskData, setTaskData] = useState({
    assetName: "",
    description: "",
    initiated: initiated,
    lastScan: lastScan,
    references: inputFields,
    stack: "",
    osiLayers: [],
    osiDetails: {} // Store details for each selected layer
  });

  const [alertVisible, setAlertVisible] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertColor, setAlertColor] = useState("");
  const [modal, setModal] = useState(false); // Modal state

  // Handle OSI layer selection
  const handleOsiLayerChange = (layerId) => {
    const updatedLayers = taskData.osiLayers.includes(layerId)
      ? taskData.osiLayers.filter(id => id !== layerId)
      : [...taskData.osiLayers, layerId];
    
    setTaskData({
      ...taskData,
      osiLayers: updatedLayers
    });
  };

  // Handle OSI layer details
  const handleOsiDetailsChange = (layerId, details) => {
    setTaskData({
      ...taskData,
      osiDetails: {
        ...taskData.osiDetails,
        [layerId]: details
      }
    });
  };

  // Previous handlers remain the same
  const initiatedChange = (date) => {
    setinitiated(date);
    setTaskData((prevData) => ({ ...prevData, initiated: date }));
  };

  const lastScanChange = (date) => {
    setlastScan(date);
    setTaskData((prevData) => ({ ...prevData, lastScan: date }));
  };

  function handleAddFields() {
    const item1 = { name: "", file: "" };
    setinputFields([...inputFields, item1]);
    setTaskData((prevData) => ({
      ...prevData,
      references: [...inputFields, item1],
    }));
  }

  function handleRemoveFields(idx) {
    document.getElementById("nested" + idx).style.display = "none";
    const newFields = inputFields.filter((_, index) => index !== idx);
    setinputFields(newFields);
    setTaskData((prevData) => ({
      ...prevData,
      references: newFields,
    }));
  }

  const handleSubmit = async (event) => {
    if (event) event.preventDefault(); // Prevent default behavior if event is provided
  
    const formData = new FormData();
    const obj = JSON.parse(localStorage.getItem("authUser"));
    const userId = obj.username;
  
    formData.append("userId", userId);
    formData.append("assetName", taskData.assetName);
    formData.append("description", taskData.description);
    formData.append("stack", taskData.stack);
    formData.append("initiated", taskData.initiated);
    formData.append("lastScan", taskData.lastScan);
    formData.append("status", "New");
    formData.append("osiLayers", JSON.stringify(taskData.osiLayers));
    formData.append("osiDetails", JSON.stringify(taskData.osiDetails));
  
    inputFields.forEach((field, index) => {
      if (field.file) {
        formData.append("file", field.file);
      }
    });
  
    try {
      const response = await fetch("https://api.bluhawk.live/threat-modeling", {
        method: "POST",
        body: formData,
      });
  
      const result = await response.json();
  
      setAlertColor("success");
      setAlertMessage("Task created successfully!");
      setAlertVisible(true);
  
      setTaskData({
        assetName: "",
        description: "",
        stack: "",
        initiated: new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' }),
        lastScan: new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' }),
        references: [{ name: "", file: "" }],
        osiLayers: [],
        osiDetails: {}
      });
      setinputFields([{ name: "", file: "" }]);
      const fileInputs = document.querySelectorAll("input[type='file']");
      fileInputs.forEach(input => {
        input.value = "";
      });
    } catch (error) {
      setAlertColor("danger");
      setAlertMessage("Something went wrong. Please try again.");
      setAlertVisible(true);
    }
  
    setTimeout(() => {
      setAlertVisible(false);
    }, 3000);
  };
  
  const toggleModal = () => {
    setModal(!modal);
  };

  const confirmSubmit = () => {
    handleSubmit();
    toggleModal(); // Close modal after submitting
  };

  return (
    <>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Threat Modelling" breadcrumbItem="AI Threat Modelling" />

          {alertVisible && (
            <UncontrolledAlert color={alertColor} className="alert-dismissible fade show" role="alert">
              {alertMessage}
            </UncontrolledAlert>
          )}

          <Row>
            <Col lg="12">
              <Card>
                <CardBody>
                  <form onSubmit={(e) => e.preventDefault()} className="outer-repeater">
                    <div data-repeater-list="outer-group" className="outer">
                      <div data-repeater-item className="outer">
                        {/* Existing form groups remain the same */}
                        <FormGroup className="mb-4" row>
                          <Label htmlFor="taskname" className="col-form-label col-lg-2">
                            Asset Name
                          </Label>
                          <Col lg="10">
                            <Input
                              id="taskname"
                              name="taskname"
                              type="text"
                              className="form-control"
                              placeholder="Enter Asset Name"
                              value={taskData.assetName}
                              onChange={(e) => {
                                setTaskData({
                                  ...taskData,
                                  assetName: e.target.value,
                                });
                              }}
                            />
                          </Col>
                        </FormGroup>

                        <FormGroup className="mb-4" row>
                          <Label htmlFor="description" className="col-form-label col-lg-2">
                            Description
                          </Label>
                          <Col lg="10">
                            <Input
                              id="description"
                              name="description"
                              type="textarea"
                              className="form-control"
                              rows="10"
                              placeholder="Enter a detailed description"
                              style={{ resize: "none" }}
                              value={taskData.description}
                              onChange={(e) => {
                                setTaskData({
                                  ...taskData,
                                  description: e.target.value,
                                });
                              }}
                            />
                          </Col>
                        </FormGroup>

                        <FormGroup className="mb-4" row>
                          <Label htmlFor="new_box" className="col-form-label col-lg-2">
                            Stack, Layers and Connectivity
                          </Label>
                          <Col lg="10">
                            <Input
                              id=""
                              name="stack"
                              type="textarea"
                              className="form-control"
                              rows="5"
                              placeholder="Enter a detailed stack, layers and connectivity details"
                              style={{ resize: "none" }}
                              value={taskData.stack}
                              onChange={(e) => {
                                setTaskData({
                                  ...taskData,
                                  stack: e.target.value,
                                });
                              }}
                            />
                          </Col>
                        </FormGroup>

                        {/* New OSI Layers section */}
                        <FormGroup className="mb-4" row>
                          <Label className="col-form-label col-lg-2">
                            OSI Layers
                          </Label>
                          <Col lg="10">
                            <div className="mb-3">
                              <p className="text-muted">Select the OSI layers relevant to your asset:</p>
                              {osiLayers.map((layer) => (
                                <div key={layer.id} className="mb-3">
                                  <div className="form-check">
                                    <Input
                                      type="checkbox"
                                      className="form-check-input"
                                      id={`layer${layer.id}`}
                                      checked={taskData.osiLayers.includes(layer.id)}
                                      onChange={() => handleOsiLayerChange(layer.id)}
                                    />
                                    <Label className="form-check-label" htmlFor={`layer${layer.id}`}>
                                      Layer {layer.id}: {layer.name}
                                    </Label>
                                  </div>
                                  {taskData.osiLayers.includes(layer.id) && (
                                    <Input
                                      type="textarea"
                                      className="form-control mt-2"
                                      placeholder={`Describe specific ${layer.name} layer components and potential vulnerabilities...`}
                                      value={taskData.osiDetails[layer.id] || ''}
                                      onChange={(e) => handleOsiDetailsChange(layer.id, e.target.value)}
                                      style={{ marginLeft: '20px', width: '95%' }}
                                    />
                                  )}
                                </div>
                              ))}
                            </div>
                          </Col>
                        </FormGroup>

                        {/* Existing reference files section */}
                        <div className="inner-repeater mb-4">
                          <div className="inner form-group mb-0 row">
                            <Label className="col-form-label col-lg-2">
                              Add Reference Files
                            </Label>
                            <div className="inner col-lg-10 ml-md-auto" id="repeater">
                              {inputFields.map((field, key) => (
                                <div
                                  key={key}
                                  id={"nested" + key}
                                  className="mb-3 row align-items-center"
                                >
                                  <Col md="6">
                                    <input
                                      type="text"
                                      className="inner form-control"
                                      value={field.name}
                                      onChange={(e) => {
                                        const newFields = [...inputFields];
                                        newFields[key].name = e.target.value;
                                        setinputFields(newFields);
                                        setTaskData({
                                          ...taskData,
                                          references: newFields,
                                        });
                                      }}
                                      placeholder="Reference Name"
                                    />
                                  </Col>
                                  <Col md="4">
                                    <div className="mt-4 mt-md-0">
                                      <Input
                                        type="file"
                                        className="form-control"
                                        onChange={(e) => {
                                          const newFields = [...inputFields];
                                          newFields[key].file = e.target.files[0];
                                          setinputFields(newFields);
                                          setTaskData({
                                            ...taskData,
                                            references: newFields,
                                          });
                                        }}
                                      />
                                    </div>
                                  </Col>
                                  <Col md="2">
                                    <div className="mt-2 mt-md-0 d-grid">
                                      <Button
                                        color="primary"
                                        className="inner"
                                        onClick={() => handleRemoveFields(key)}
                                        block
                                      >
                                        Delete
                                      </Button>
                                    </div>
                                  </Col>
                                </div>
                              ))}
                            </div>
                          </div>
                          <Row className="justify-content-end">
                            <Col lg="10">
                              <Button
                                color="success"
                                className="inner"
                                onClick={handleAddFields}
                              >
                                Add Reference
                              </Button>
                            </Col>
                          </Row>
                        </div>

                        <Row>
                          <Col lg="12">
                            <div style={{ borderBottom: "1px solid #ccc", margin: "20px 0" }}></div>
                          </Col>
                        </Row>

                        <Row className="justify-content-end">
                          <Col lg="12">
                            <Button
                              color="primary"
                              onClick={toggleModal}
                            >
                              Create Task
                            </Button>
                          </Col>
                        </Row>
                      </div>
                    </div>
                  </form>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <Modal isOpen={modal} toggle={toggleModal}>
  <ModalHeader toggle={toggleModal}>Confirm Submission</ModalHeader>
  <ModalBody>
    <div>
      <h5>Asset Name: {taskData.assetName}</h5>
      <p>Description: {taskData.description}</p>
      <p>Stack: {taskData.stack}</p>
      <div>
        <h6>Selected OSI Layers:</h6>
        {taskData.osiLayers.length === 0 ? (
          <p>No layers selected</p>
        ) : (
          taskData.osiLayers.map((layerId) => {
            const layer = osiLayers.find((l) => l.id === layerId);
            const layerDetails = taskData.osiDetails[layerId] || "No additional details provided.";

            // Define colors for each layer
            const layerColors = {
              7: "#a7c7e7", // Pastel blue
              6: "#c2c2c2", // Pastel gray
              5: "#b2e0c5", // Pastel green
              4: "#f5b7b1", // Pastel red
              3: "#ffeeba", // Pastel yellow
              2: "#b2dbe9", // Pastel teal
              1: "#f4f4f4", // Pastel white
            };

            return (
              <div
                key={layer.id}
                style={{
                  backgroundColor: layerColors[layer.id],
                  color: layer.id === 1 ? "#000" : "#fff", // Use dark text for light backgrounds
                  padding: "10px",
                  margin: "10px 0",
                  borderRadius: "5px",
                  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                }}
              >
<strong style={{ color: 'black' }}>Layer {layer.name}</strong>
<span style={{ color: 'black' }}> : {layerDetails}</span>


              </div>
            );
          })
        )}
      </div>
    </div>
  </ModalBody>
  <ModalFooter>
    <Button color="secondary" onClick={toggleModal}>Cancel</Button>
    <Button color="primary" onClick={confirmSubmit}>Confirm</Button>
  </ModalFooter>
</Modal>

    </>
  );
};

export default TMcreate;
